import { Injectable } from '@angular/core';
import { GraphNode, GraphEdge, Statement } from '../models/chat.models';
import { GraphDataService } from './graph-data.service';
import { ProcessedGraphData } from '../models/chat.models';
import { ChatStateService } from './chat-state.service';
import { Observable } from 'rxjs';

@Injectable()
export class GraphModalService {
  isModalOpen = false;
  isGraphLoading = false;
  graphNodes: GraphNode[] = [];
  graphEdges: GraphEdge[] = [];
  selectedNode: GraphNode | null = null;
  selectedEdge: GraphEdge | null = null;

  constructor(
    private graphDataService: GraphDataService,
    private chatState: ChatStateService
  ) {}

  handleInlineReferenceClick(statement: Statement): void {
    if (statement?.statementId) {
      this.handleReferenceClick(statement.statementId);
    } else if (statement?.chunkId) {
      this.handleChunkReferenceClick(statement.chunkId);
    } else {
      console.warn('Reference has neither statementId nor chunkId', statement);
    }
  }

  handleReferenceClick(statementId: string): void {
    if (!statementId) {
      console.warn('Attempted to fetch graph for null/undefined statementId');
      return;
    }

    this.fetchAndDisplayGraph(
      this.graphDataService.getGraphData(statementId),
      'Failed to fetch graph data'
    );
  }

  handleChunkReferenceClick(chunkId: string): void {
    if (!chunkId) {
      console.warn('Attempted to fetch graph for null/undefined chunkId');
      return;
    }

    this.fetchAndDisplayGraph(
      this.graphDataService.getChunkGraphData(chunkId),
      'Failed to fetch graph data'
    );
  }

  handleShowAllResources(statements: any[]): void {
    const uniqueStatementIds = [...new Set(
      statements.map(stmt => stmt.statementId).filter((id: string | undefined): id is string => !!id)
    )];
    // Chunk-backed citations have no statementId; include their chunks instead.
    const uniqueChunkIds = [...new Set(
      statements
        .filter(stmt => !stmt.statementId && stmt.chunkId)
        .map(stmt => stmt.chunkId as string)
    )];
    if (uniqueStatementIds.length === 0 && uniqueChunkIds.length === 0) return;

    this.fetchAndDisplayGraph(
      this.graphDataService.getAllGraphData(uniqueStatementIds, uniqueChunkIds),
      'Failed to fetch all graph data'
    );
  }

  handleNodeClick(node: GraphNode): void {
    this.selectedNode = node;
    this.selectedEdge = null; // Deselect edge when node is selected
  }

  handleEdgeClick(edge: GraphEdge): void {
    this.selectedNode = null;
    this.selectedEdge = edge;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedNode = null;
    this.selectedEdge = null;
  }

  private fetchAndDisplayGraph(apiCall: Observable<ProcessedGraphData>, errorMessage: string): void {
    this.isModalOpen = true;
    this.isGraphLoading = true;
    this.chatState.clearNotifications();
    this.selectedNode = null;
    this.selectedEdge = null;

    apiCall.subscribe({
      next: (response: ProcessedGraphData) => {
        if (!response.nodes || response.nodes.length === 0) {
          this.chatState.setError('Received empty graph data from backend.');
          this.graphNodes = [];
          this.graphEdges = [];
        } else {
          this.graphNodes = response.nodes;
          this.graphEdges = response.edges;
        }
        this.isGraphLoading = false;
      },
      error: (err: any) => {
        console.error(errorMessage, err);
        const errMsg = err?.message || err?.error || 'Unknown error';
        this.chatState.setError(`${errorMessage}: ${errMsg}`);
        this.isModalOpen = false;
        this.isGraphLoading = false;
      }
    });
  }
}
